import React, { useEffect, useState } from "react";
import "@/styles/Layout.scss";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import Spinner from "@/components/Spinner";
import { getUser } from "@/hooks/useLocalStorage";
import apiClient from "@/services";
import { useNavigate } from "react-router-dom";
import { handleImageUpload } from "@/scripts/uploadImages";
import getConstants from "@/scripts/getConstants";

const formSchema = z.object({
  grade: z.string().min(2, { message: "Grade of material is required" }),
  form: z.string().min(2, { message: "Form of material is required" }),
  location: z.string().optional(),
  source: z.string().optional(),
  postalCode: z.string().optional(),
  terms: z.boolean().refine((value) => value === true, {
    message: "Please accept the terms and conditions to proceed.",
  }),
});

function BuyerProductForm() {
  const user = getUser();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  // Constants
  const [forms, setForms] = useState([]);
  const [grades, setGrades] = useState([]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      grade: "",
      form: "",
      location: "",
      source: "",
      postalCode: "",
      terms: false,
    },
  });

  const onSubmit = async (values) => {
    // Clients email check - for Buyer Agents
    if ((user.role === "buyer_agent" || user.role === "admin") && !email) {
      alert("Client's email is required!");
      return;
    }

    if (images.length > 5) {
      alert("You can only upload up to 5 images!");
      return;
    }

    try {
      setLoading(true);

      let imageData;
      // Upload images and handle errors explicitly
      try {
        imageData = await handleImageUpload(images, "buyer-products", user.uid);
      } catch (error) {
        alert("Error uploading images: " + (error.message || error));
        throw error; // Stop further execution if image upload fails
      }

      // Send form data along with the uploaded image URLs
      const { status } = await apiClient.post("/createBuyerRequest", {
        product: { ...values, images: imageData },
        email: email || "", // for agents to set user_id
        userRole: user.role,
      });

      if (status === 201) {
        alert("Product form submitted successfully!");
        console.log("Buyer request created successfully");
        navigate("/home");
      } else {
        throw new Error(`Unexpected response status: ${status}`);
      }
    } catch (error) {
      if (error.status === 400) {
        alert(
          "No user found with the provided client email. Please enter a valid email linked to a buyer or seller account!"
        );
      } else {
        console.error("Error submitting buyer request:", error);
        alert("Failed to submit form. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFile = (e) => {
    const formattedFiles = Array.from(e.target.files).map((file) => ({
      file,
    }));

    setImages(formattedFiles);
  };

  const fetchConstants = async () => {
    try {
      setLoading(true);
      const { forms, grades } = await getConstants();
      // Set values
      setForms(forms);
      setGrades(grades);
    } catch (err) {
      console.log("Unable to fetch constants", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConstants();
  }, []);

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="sectionWrapper mobileWrapper">
      <div>
        <h1 className="text-sm font-bold text-green ml-0.5">Product Form</h1>
        <div className="highlight highlight-md" />
      </div>

      <p className="mt-3 ml-3 text-sm text-muted mobileMargin">
        Interested in Buying? Fill out this form to discover the products you
        need.
      </p>

      <Form {...form}>
        <form className="space-y-4 p-3" onSubmit={form.handleSubmit(onSubmit)}>
          {(user.role === "buyer_agent" || user.role === "admin") && (
            <div className="flex justify-between mobileFormWrapper">
              <div className="w-[49%] mobileInputWrapper">
                <FormItem>
                  <FormLabel>Client's Email*</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="Enter client's email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </div>
            </div>
          )}

          <div className="flex justify-between mobileFormWrapper">
            {/* Grade */}
            <div className="w-[49%] mobileInputWrapper">
              <FormField
                control={form.control}
                name="grade"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Grade of Material*</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl className="h-10">
                        <SelectTrigger>
                          <SelectValue placeholder="Select grade" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {grades.map((grade) => (
                          <SelectItem key={grade} value={grade}>
                            {grade}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Form */}
            <div className="w-[49%] mobileInputWrapper">
              <FormField
                control={form.control}
                name="form"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Form of Material*</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl className="h-10">
                        <SelectTrigger>
                          <SelectValue placeholder="Select form" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {forms.map((form) => (
                          <SelectItem key={form} value={form}>
                            {form}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="flex justify-between">
            <div className="w-[49%] mobileInputWrapper">
              {/* Postal code */}
              <FormField
                control={form.control}
                name="postalCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Postal Code</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter postal code" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="w-[49%] mobileInputWrapper">
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter City, State, Country"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="flex justify-between">
            <div className="w-[49%] mobileInputWrapper">
              {/* Source of inquiry */}
              <div className="w-[100%] mobileInputWrapper">
                <FormField
                  control={form.control}
                  name="source"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Source of inquiry</FormLabel>
                      <FormControl>
                        <Input placeholder="Link to source of inquiry" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="w-[49%] mobileInputWrapper">
              {/* Product Image */}
              <FormItem>
                <FormLabel>Upload Image</FormLabel>
                <FormControl className="text-muted pt-2">
                  <Input
                    id="images"
                    type="file"
                    multiple
                    onChange={(e) => handleFile(e)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            </div>
          </div>

          <FormField
            control={form.control}
            name="terms"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-3 text-muted">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="mobileFont">
                    By submitting this form, you grant JP Associates the right
                    to use the submitted data and photos for evaluation,
                    communication, and promotional purposes. All information
                    provided will be handled in accordance with our{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      className="text-blue-500"
                    >
                      Privacy Policy
                    </a>
                    .
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <div className="text-right">
            <Button
              type="submit"
              className="w-[200px] bg-green mobileInputWrapper"
            >
              Submit
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

export default BuyerProductForm;
